"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { QRCode } from "react-qrcode-logo";
import { cn } from "@/lib/utils";
import type { Image as GalleryImage } from "@/types";
import PhotowallArrivals from "@/components/upload/PhotowallArrivals";
import { usePhotowallArrivals, type PhotowallArrivalDirection, type PhotowallArrivalStyle } from "@/components/upload/usePhotowallArrivals";
import { QR_POSITIONS, type ImageOrderMode, type QrPosition, type RotationMode, type TransitionSpeed } from "@/components/upload/photowall-setup/photowallSetupTypes";
import { DEMO_COVER_PHOTO, DEMO_PHOTOS, loadDemoPhoto, preloadDemoPhotos, type DemoPhoto } from "./photowallDemoPhotos";
import styles from "./PhotowallDemoWall.module.css";
export interface DemoWallSettings {
  qrPosition: QrPosition | null;
  imageOrder: ImageOrderMode;
  rotationMode: RotationMode;
  speed: TransitionSpeed;
  arrivalStyle: PhotowallArrivalStyle;
  arrivalDirection: PhotowallArrivalDirection;
}
export const DEFAULT_DEMO_WALL_SETTINGS: DemoWallSettings = {
  qrPosition: "left-bottom",
  imageOrder: "mixed",
  rotationMode: "dynamic",
  speed: "best",
  arrivalStyle: "polaroid",
  arrivalDirection: "bottom-to-top"
};

// The same rhythm as the live wall's speed setting.
export const DEMO_ROTATION_INTERVAL_MS: Record<TransitionSpeed, number> = {
  slower: 12000,
  best: 8000,
  faster: 4000
};

// The live wall is laid out for a laptop screen; the demo scales down from it.
const REFERENCE_SCREEN_WIDTH = 1440;
const LOOKAHEAD = 6;
const FADE_EXIT_MS = 1200;
const WIPE_EXIT_MS = 2000;
const QR_POSITION_LABEL_KEYS = {
  "left-top": "position.topLeft",
  "right-top": "position.topRight",
  "left-bottom": "position.bottomLeft",
  "right-bottom": "position.bottomRight"
} as const satisfies Record<QrPosition, string>;
interface DemoLayout {
  photos: DemoPhoto[];
  className: string;
  shape: string;
  isContained?: boolean;
}
interface DemoLayer {
  id: number;
  layout: DemoLayout;
  isWipe: boolean;
}
interface LayoutCandidate {
  weight: number;
  layout: DemoLayout;
}
interface PhotowallDemoWallProps {
  images: GalleryImage[];
  settings: DemoWallSettings;
  label: string;
  isPaused?: boolean;
  isWaiting?: boolean;
  qrValue?: string;
  /** Which of the fixed opening layouts to show, so neighbouring walls differ. */
  firstLayout?: number;
  onCornerSelect?: (position: QrPosition) => void;
  className?: string;
}

// Deterministic so the server and the first client render agree.
const FIRST_LAYOUTS: DemoLayout[] = [{
  photos: [DEMO_PHOTOS[5], DEMO_PHOTOS[9]],
  className: styles.layoutMixedLeft,
  shape: "2:mixed-left"
}, {
  photos: [DEMO_PHOTOS[7], DEMO_PHOTOS[8], DEMO_PHOTOS[22]],
  className: styles.layout3,
  shape: "3"
}, {
  photos: [DEMO_PHOTOS[21], DEMO_PHOTOS[13]],
  className: styles.layoutMixedRight,
  shape: "2:mixed-right"
}];
function getLayoutCandidates(photos: DemoPhoto[]): LayoutCandidate[] {
  const tall = photos.filter(photo => photo.aspectRatio < 0.8);
  const wide = photos.filter(photo => photo.aspectRatio >= 0.8);
  const candidates: LayoutCandidate[] = [];

  // Weights follow determineLayout in the live wall.
  if (wide.length >= 4) {
    candidates.push({
      weight: 28,
      layout: {
        photos: wide.slice(0, 4),
        className: styles.layout4,
        shape: "4"
      }
    });
  } else if (photos.length >= 4) {
    candidates.push({
      weight: 16,
      layout: {
        photos: photos.slice(0, 4),
        className: styles.layout4,
        shape: "4"
      }
    });
  }
  if (tall.length >= 3) {
    candidates.push({
      weight: 32,
      layout: {
        photos: tall.slice(0, 3),
        className: styles.layout3,
        shape: "3"
      }
    });
  }
  if (tall.length >= 2) {
    candidates.push({
      weight: 24,
      layout: {
        photos: tall.slice(0, 2),
        className: styles.layout2,
        shape: "2"
      }
    });
  }
  if (wide.length >= 2) {
    candidates.push({
      weight: 20,
      layout: {
        photos: wide.slice(0, 2),
        className: styles.layout2,
        shape: "2"
      }
    });
  }
  if (tall.length >= 1 && wide.length >= 1) {
    const isWideFirst = Math.random() < 0.5;
    candidates.push({
      weight: 28,
      layout: isWideFirst ? {
        photos: [wide[0], tall[0]],
        className: styles.layoutMixedLeft,
        shape: "2:mixed-left"
      } : {
        photos: [tall[0], wide[0]],
        className: styles.layoutMixedRight,
        shape: "2:mixed-right"
      }
    });
  }
  if (photos.length >= 1) {
    candidates.push({
      weight: 10,
      layout: {
        photos: [photos[0]],
        className: styles.layout1,
        shape: "1"
      }
    });
  }
  return candidates;
}
function chooseLayout(photos: DemoPhoto[], history: string[]): DemoLayout {
  const recent = history.slice(-6);
  const weighted = getLayoutCandidates(photos).map(candidate => {
    const recentUses = recent.filter(shape => shape === candidate.layout.shape).length;
    let consecutiveUses = 0;
    for (let index = recent.length - 1; index >= 0 && recent[index] === candidate.layout.shape; index--) {
      consecutiveUses++;
    }
    // Repeats are briefly favoured so alternating wipes can play, then capped.
    const repeatFactor = consecutiveUses >= 3 ? 0 : consecutiveUses > 0 ? 3 : recentUses >= 2 ? 0.35 : 1;
    return {
      ...candidate,
      weight: candidate.weight * repeatFactor
    };
  });
  const eligible = weighted.filter(candidate => candidate.weight > 0);
  const total = eligible.reduce((sum, candidate) => sum + candidate.weight, 0);
  let roll = Math.random() * total;
  for (const candidate of eligible) {
    roll -= candidate.weight;
    if (roll <= 0) return candidate.layout;
  }
  return eligible[0]?.layout ?? FIRST_LAYOUTS[0];
}
function createPhotoCycle(order: ImageOrderMode): DemoPhoto[] {
  return order === "timeline" ? [...DEMO_PHOTOS].sort((a, b) => a.takenOrder - b.takenOrder) : [...DEMO_PHOTOS].sort(() => Math.random() - 0.5);
}
function getCornerStyle(position: QrPosition, offset: number): React.CSSProperties {
  const [horizontal, vertical] = position.split("-") as ["left" | "right", "top" | "bottom"];
  return {
    [horizontal]: offset,
    [vertical]: offset
  };
}

/** A scaled-down live photo wall that runs on the published demo photos. */
export default function PhotowallDemoWall(t0) {
  const $ = _c(63);
  const {
    images,
    settings,
    label,
    isPaused: t1,
    isWaiting: t2,
    qrValue: t3,
    firstLayout: t4,
    onCornerSelect,
    className
  } = t0;
  const isPaused = t1 === undefined ? false : t1;
  const isWaiting = t2 === undefined ? false : t2;
  const qrValue = t3 === undefined ? "https://guest.gallery" : t3;
  const firstLayout = t4 === undefined ? 0 : t4;
  const t = useTranslations("Upload.Photowall");
  const wallRef = useRef(null);
  const [wallWidth, setWallWidth] = useState(0);
  const [isOnScreen, setIsOnScreen] = useState(false);
  const openingLayout = FIRST_LAYOUTS[firstLayout % FIRST_LAYOUTS.length];
  let t5;
  if ($[0] !== openingLayout) {
    t5 = [{
      id: 0,
      layout: openingLayout,
      isWipe: false
    }];
    $[0] = openingLayout;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const [layers, setLayers] = useState(t5);
  let t6;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const queueRef = useRef(t6);
  let t7;
  if ($[3] !== openingLayout.shape) {
    t7 = [openingLayout.shape];
    $[3] = openingLayout.shape;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const historyRef = useRef(t7);
  const layerIdRef = useRef(1);
  const isRunning = !isPaused && !isWaiting && isOnScreen;
  const t8 = settings.arrivalStyle !== "off";
  let t9;
  if ($[5] !== images || $[6] !== isRunning || $[7] !== t8) {
    t9 = {
      images,
      shouldAnnounce: true,
      isActive: true,
      isPlaying: isRunning,
      isEnabled: t8
    };
    $[5] = images;
    $[6] = isRunning;
    $[7] = t8;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const {
    flights,
    removeFlight,
    isReducedMotion
  } = usePhotowallArrivals(t9);
  let t10;
  let t11;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      preloadDemoPhotos();
      const element = wallRef.current;
      if (!element) {
        return;
      }
      const resizeObserver = new ResizeObserver(t12 => {
        const [entry] = t12;
        setWallWidth(entry.contentRect.width);
      });
      const intersectionObserver = new IntersectionObserver(t13 => {
        const [entry_0] = t13;
        setIsOnScreen(entry_0.isIntersecting);
      });
      resizeObserver.observe(element);
      intersectionObserver.observe(element);
      return () => {
        resizeObserver.disconnect();
        intersectionObserver.disconnect();
      };
    };
    t11 = [];
    $[9] = t10;
    $[10] = t11;
  } else {
    t10 = $[9];
    t11 = $[10];
  }
  useEffect(t10, t11);
  let t12;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      queueRef.current = [];
    };
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  let t13;
  if ($[12] !== settings.imageOrder) {
    t13 = [settings.imageOrder];
    $[12] = settings.imageOrder;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  useEffect(t12, t13);
  let t14;
  let t15;
  if ($[14] !== isRunning || $[15] !== settings.imageOrder || $[16] !== settings.rotationMode || $[17] !== settings.speed) {
    t14 = () => {
      if (!isRunning) {
        return;
      }
      let isCancelled = false;
      const exitTimers = new Set();
      const advance = () => {
        while (queueRef.current.length < LOOKAHEAD) {
          queueRef.current.push(...createPhotoCycle(settings.imageOrder));
        }
        const lookahead = queueRef.current.slice(0, LOOKAHEAD).filter(_temp);
        const layout = settings.rotationMode === "dynamic" ? chooseLayout(lookahead, historyRef.current) : {
          photos: [lookahead[0]],
          className: styles.layout1,
          shape: "1",
          isContained: settings.rotationMode === "single-contain"
        };
        layout.photos.forEach(photo_0 => {
          const index_0 = queueRef.current.findIndex(queued => queued.id === photo_0.id);
          if (index_0 >= 0) {
            queueRef.current.splice(index_0, 1);
          }
        });
        const previousShape = historyRef.current[historyRef.current.length - 1];
        historyRef.current = [...historyRef.current, layout.shape].slice(-12);
        const canWipe = previousShape === layout.shape && (layout.photos.length === 2 || layout.photos.length === 3);
        const isWipe = canWipe && Math.random() >= 0.25;
        layerIdRef.current = layerIdRef.current + 1;
        const id = layerIdRef.current;
        Promise.all(layout.photos.map(_temp2)).then(() => {
          if (isCancelled) {
            return;
          }
          setLayers(current => [...current.slice(-1), {
            id,
            layout,
            isWipe
          }]);
          const timer = setTimeout(() => {
            exitTimers.delete(timer);
            setLayers(current_0 => current_0.filter(layer => layer.id >= id));
          }, isWipe ? WIPE_EXIT_MS : FADE_EXIT_MS);
          exitTimers.add(timer);
        });
      };
      const interval = setInterval(advance, DEMO_ROTATION_INTERVAL_MS[settings.speed]);
      return () => {
        isCancelled = true;
        clearInterval(interval);
        exitTimers.forEach(_temp3);
      };
    };
    t15 = [isRunning, settings.imageOrder, settings.rotationMode, settings.speed];
    $[14] = isRunning;
    $[15] = settings.imageOrder;
    $[16] = settings.rotationMode;
    $[17] = settings.speed;
    $[18] = t14;
    $[19] = t15;
  } else {
    t14 = $[18];
    t15 = $[19];
  }
  useEffect(t14, t15);
  const scale = wallWidth / REFERENCE_SCREEN_WIDTH;
  let t16;
  if ($[20] !== scale) {
    t16 = Math.round(150 * scale);
    $[20] = scale;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  const qrSize = Math.max(28, t16);
  let t17;
  if ($[22] !== scale) {
    t17 = Math.round(16 * scale);
    $[22] = scale;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  const qrPadding = Math.max(4, t17);
  let t18;
  if ($[24] !== scale) {
    t18 = Math.round(32 * scale);
    $[24] = scale;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  const qrOffset = Math.max(6, t18);
  const t19 = onCornerSelect ? "group" : "img";
  let t20;
  if ($[26] !== className) {
    t20 = cn(styles.wall, className);
    $[26] = className;
    $[27] = t20;
  } else {
    t20 = $[27];
  }
  let t21;
  if ($[28] !== isWaiting || $[29] !== layers) {
    t21 = isWaiting ? <div className={styles.waiting}><img src={DEMO_COVER_PHOTO.url} alt="" className={styles.waitingCover} /><div className={styles.waitingShade} /></div> : layers.map(_temp5);
    $[28] = isWaiting;
    $[29] = layers;
    $[30] = t21;
  } else {
    t21 = $[30];
  }
  let t22;
  if ($[31] !== flights || $[32] !== isReducedMotion || $[33] !== isRunning || $[34] !== isWaiting || $[35] !== removeFlight || $[36] !== settings.arrivalDirection || $[37] !== settings.arrivalStyle || $[38] !== settings.qrPosition || $[39] !== wallWidth) {
    t22 = !isWaiting && settings.arrivalStyle !== "off" && wallWidth > 0 && <PhotowallArrivals flights={flights} style={settings.arrivalStyle} direction={settings.arrivalDirection} qrPosition={settings.qrPosition} isReducedMotion={isReducedMotion} isPaused={!isRunning} onComplete={removeFlight} />;
    $[31] = flights;
    $[32] = isReducedMotion;
    $[33] = isRunning;
    $[34] = isWaiting;
    $[35] = removeFlight;
    $[36] = settings.arrivalDirection;
    $[37] = settings.arrivalStyle;
    $[38] = settings.qrPosition;
    $[39] = wallWidth;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== isWaiting || $[42] !== qrOffset || $[43] !== qrPadding || $[44] !== qrSize || $[45] !== qrValue || $[46] !== settings.qrPosition || $[47] !== t || $[48] !== wallWidth) {
    t23 = settings.qrPosition && wallWidth > 0 && (isWaiting ? <div className={styles.waitingPanel} style={getCornerStyle(settings.qrPosition, qrOffset)}><div className="max-w-[24cqi] pl-[1cqi]"><p>{t("beOneOfTheFirst")}</p><p>{t("scanQR")} →</p></div><div className={styles.qr} style={{
        position: "static",
        padding: qrPadding
      }}><QRCode value={qrValue} qrStyle="dots" eyeRadius={5} fgColor="#FFFFFF" bgColor="transparent" size={qrSize} quietZone={0} ecLevel="M" /></div></div> : <div data-photowall-qr="" className={styles.qr} style={{
      ...getCornerStyle(settings.qrPosition, qrOffset),
      padding: qrPadding
    }}><QRCode value={qrValue} qrStyle="dots" eyeRadius={5} fgColor="#FFFFFF" bgColor="transparent" size={qrSize} quietZone={0} ecLevel="M" /></div>);
    $[41] = isWaiting;
    $[42] = qrOffset;
    $[43] = qrPadding;
    $[44] = qrSize;
    $[45] = qrValue;
    $[46] = settings.qrPosition;
    $[47] = t;
    $[48] = wallWidth;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== onCornerSelect || $[51] !== settings.qrPosition || $[52] !== t) {
    t24 = onCornerSelect && QR_POSITIONS.map(position => <button key={position} type="button" aria-label={t(QR_POSITION_LABEL_KEYS[position])} aria-pressed={settings.qrPosition === position} onClick={() => onCornerSelect(position)} className={styles.corner} style={getCornerStyle(position, 0)} />);
    $[50] = onCornerSelect;
    $[51] = settings.qrPosition;
    $[52] = t;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== isWaiting || $[55] !== label || $[56] !== t19 || $[57] !== t20 || $[58] !== t21 || $[59] !== t22 || $[60] !== t23 || $[61] !== t24) {
    t25 = <div ref={wallRef} role={t19} aria-label={label} data-demo-wall="" data-waiting={isWaiting} className={t20}>{t21}{t22}{t23}{t24}</div>;
    $[54] = isWaiting;
    $[55] = label;
    $[56] = t19;
    $[57] = t20;
    $[58] = t21;
    $[59] = t22;
    $[60] = t23;
    $[61] = t24;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  return t25;
}
function _temp5(layer_0) {
  return <div key={layer_0.id} data-demo-layer={layer_0.layout.shape} className={cn(styles.layer, layer_0.layout.className, layer_0.layout.isContained && styles.contain, layer_0.isWipe && styles.wipe)}>{layer_0.layout.photos.map(_temp4)}</div>;
}
function _temp4(photo_2, index_1) {
  return <div key={photo_2.id} className={styles.cell} style={{
    animationDelay: `${index_1 * 120}ms`
  }}><div className={styles.frame}><img src={photo_2.url} alt="" className={styles.photo} style={{
        objectPosition: photo_2.focus
      }} /></div></div>;
}
function _temp3(timer_0) {
  return clearTimeout(timer_0);
}
function _temp2(photo_1) {
  return loadDemoPhoto(photo_1.url);
}
function _temp(photo, index, all) {
  return all.findIndex(other => other.id === photo.id) === index;
}